import * as React from 'react';
import { Button, InputGroup, Stack } from 'react-bootstrap';
import { Entity, Field, FieldType, ReferenceField, Row } from 'gossamer-unified';
import { CSVUploadInstructions, General, PageTitle, Section, UneditableReference } from 'gossamer-front-end';
import { Event, EventUpload, InstitutionGroup } from 'cb-model';

const TOTAL_STEPS = 5;

export const BulkUploadPromptGenerator = (): React.JSX.Element => {
  const [step, setStep] = React.useState(1);
  const [eventTypes, setEventTypes] = React.useState<string[]>([]);
  const [eventProfiles, setEventProfiles] = React.useState<string[]>([]);
  const [userIds, setUserIds] = React.useState<string[]>([]);
  const [groupIds, setGroupIds] = React.useState<string[]>([]);
  return (
    <>
      <PageTitle
        title="Bulk Upload Prompt Generator"
        subtitle="Create a prompt for an AI to help convert old timetable data into uploadable event data"
      />
      <p>
        AI can be really helpful with time-consuming data-reformatting tasks but getting the prompt right makes all the
        difference.
      </p>
      <p>Use this tool to create the right prompt for your situation.</p>
      <Section>
        {step === 1 && <Step1 setStep={setStep} eventTypes={eventTypes} setEventTypes={setEventTypes} />}
        {step === 2 && <Step2 setStep={setStep} eventProfiles={eventProfiles} setEventProfiles={setEventProfiles} />}
        {step === 3 && <Step3 setStep={setStep} userIds={userIds} setUserIds={setUserIds} />}
        {step === 4 && <Step4 setStep={setStep} groupIds={groupIds} setGroupIds={setGroupIds} />}
        {step === 5 && (
          <Step5
            setStep={setStep}
            eventTypes={eventTypes}
            eventProfiles={eventProfiles}
            userIds={userIds}
            groupIds={groupIds}
          />
        )}
      </Section>
    </>
  );
};

interface MultiValueStackProps {
  field: Field;
  values: string[];
  onChange: (newValues: string[]) => void;
}

const MultiValueStack = (props: MultiValueStackProps): React.JSX.Element => {
  const { values, onChange } = props;
  const handleDelete = (value: string) => {
    const newValues = values.filter((v) => v !== value);
    onChange(newValues);
  };
  return (
    <div>
      {values.map((value) => (
        <Stack key={'value-' + value} direction="horizontal" gap={2}>
          <button onClick={() => handleDelete(value)} className="ControlIcon" type="button">
            ✖
          </button>
          <General editable={false} fieldId={value} field={props.field} value={value} />
        </Stack>
      ))}
      <div className="mb10px" />
      <InputGroup className="mb10px" id="value-new">
        <InputGroup.Text>Add a new value:</InputGroup.Text>
        <General
          editable={true}
          field={props.field}
          fieldId="new"
          excludeValues={values}
          handleChange={(newValue) => onChange([...values, newValue || ''])}
          value=""
        />
      </InputGroup>
    </div>
  );
};

interface NavButtonsProps {
  currentStep: number;
  setStep: (step: number) => void;
}

const NavButtons = (props: NavButtonsProps): React.JSX.Element => {
  return (
    <Stack direction="horizontal" gap={2}>
      {props.currentStep > 1 && (
        <Button onClick={() => props.setStep(props.currentStep - 1)} size="sm" variant="secondary">
          Back
        </Button>
      )}
      {props.currentStep < TOTAL_STEPS && (
        <Button onClick={() => props.setStep(props.currentStep + 1)} size="sm" variant="primary">
          Next
        </Button>
      )}
    </Stack>
  );
};

const Step1 = (props: {
  setStep: (step: number) => void;
  eventTypes: string[];
  setEventTypes: (types: string[]) => void;
}): React.JSX.Element => {
  return (
    <div>
      <b>Step 1: Choose the Event Type(s) you are going to create</b>
      <MultiValueStack field={Event.fields.type} values={props.eventTypes} onChange={props.setEventTypes} />
      <NavButtons currentStep={1} setStep={props.setStep} />
    </div>
  );
};

const Step2 = (props: {
  setStep: (step: number) => void;
  eventProfiles: string[];
  setEventProfiles: (profiles: string[]) => void;
}): React.JSX.Element => {
  return (
    <div>
      <b>Step 2: Choose the Event Profile(s) you are going to use to create the events</b>
      <MultiValueStack
        field={Event.fields.event_profile_id}
        values={props.eventProfiles}
        onChange={props.setEventProfiles}
      />
      <div>
        <NavButtons currentStep={2} setStep={props.setStep} />
      </div>
    </div>
  );
};

const Step3 = (props: {
  setStep: (step: number) => void;
  userIds: string[];
  setUserIds: (ids: string[]) => void;
}): React.JSX.Element => {
  return (
    <div>
      <b>Step 3: Choose the User(s) that are going to be the main hosts for the events</b>
      <MultiValueStack field={Event.fields.main_host_user_id} values={props.userIds} onChange={props.setUserIds} />
      <div>
        <NavButtons currentStep={3} setStep={props.setStep} />
      </div>
    </div>
  );
};

const Step4 = (props: {
  setStep: (step: number) => void;
  groupIds: string[];
  setGroupIds: (ids: string[]) => void;
}): React.JSX.Element => {
  return (
    <div>
      <b>Step 4: Choose the Group(s) that the events are going to be allocated to</b>
      <MultiValueStack
        field={{
          seq: 0,
          type: FieldType.Reference,
          label: 'Group',
          refEntity: InstitutionGroup,
        }}
        values={props.groupIds}
        onChange={props.setGroupIds}
      />
      <div>
        <NavButtons currentStep={4} setStep={props.setStep} />
      </div>
    </div>
  );
};

const PROMPT_PART_1 =
  'You are helping me convert a spreadsheet of training/teaching events into a CSV file for upload to Careerbook, one row per event, no header row.';

const PROMPT_PART_2 = 'The CSV file MUST have the following columns:';
// use

const PROMPT_PART_3 = 'Each Event Type MUST be one of the following: ';
const PROMPT_PART_4 = 'Each Event Profile Id MUST be one of the following ids: ';
const PROMPT_PART_5 = 'Each Main Host User Id MUST be one of the following ids: ';
const PROMPT_PART_6 =
  'The Group Id(s) used MUST be of the following ids (there can be multiple in each row, delimited with a |): ';
const PROMPT_PART_7 =
  'Important - if and only if a required column is missing, ambiguous, \
or unclear for one or more rows, stop and ask me a clarifying question about those specific \
rows before producing the CSV. Do not guess and do not silently leave cells blank. Once every \
unclear point is resolved, give me the finished result as a single downloadable CSV file.';

const Step5 = (props: {
  setStep: (step: number) => void;
  eventTypes: string[];
  eventProfiles: string[];
  userIds: string[];
  groupIds: string[];
}): React.JSX.Element => {
  return (
    <div>
      <b>Step 5: Here is the prompt to use</b>
      <div>
        {PROMPT_PART_1}
        <br />
        {PROMPT_PART_2}
        <CSVUploadInstructions entity={EventUpload} />
        <br />
        {PROMPT_PART_3} {props.eventTypes.join(', ')}
        <br />
        {PROMPT_PART_4}
        <IdList refEntity={(Event.fields.event_profile_id as ReferenceField).refEntity} ids={props.eventProfiles} />
        <br />
        {PROMPT_PART_5}
        <IdList refEntity={(Event.fields.main_host_user_id as ReferenceField).refEntity} ids={props.userIds} />
        <br />
        {PROMPT_PART_6}
        <IdList refEntity={InstitutionGroup} ids={props.groupIds} />
        <br />
        {PROMPT_PART_7}
      </div>
    </div>
  );
};

interface IdListProps {
  refEntity: Entity<Row>;
  ids: string[];
}

const IdList = (props: IdListProps): React.JSX.Element => {
  return (
    <>
      {props.ids.map((id) => (
        <div key={id}>
          {id} =
          <UneditableReference refEntity={props.refEntity} suppressLink={true} value={id} />
        </div>
      ))}
    </>
  );
};
